import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, PauseCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { PAUSE_MESSAGE } from '../lib/checkoutStatus';

const SuccessView = ({ onClose }) => (
  <div className="text-center py-4" data-testid="waitlist-success">
    <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-3" />
    <h3 className="text-xl font-bold text-gray-900">You're on the waitlist</h3>
    <p className="text-sm text-gray-600 mt-2 leading-relaxed">
      Thank you. We'll email you as soon as we reopen. Nothing has been charged and no delivery has been scheduled.
    </p>
    <Button onClick={onClose} className="mt-5 bg-amber-600 hover:bg-amber-700 text-white" data-testid="waitlist-close-btn">
      Close
    </Button>
  </div>
);

export const WaitlistDialog = ({ open, onOpenChange, defaults = {}, source = '', interest = '' }) => {
  const [form, setForm] = useState({ name: '', email: '', prayer_text: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm({ name: defaults.name || '', email: defaults.email || '', prayer_text: defaults.prayer_text || '' });
      setStatus('idle');
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      setError('Please enter your name and a valid email.');
      return;
    }
    setStatus('loading');
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, dedicated_to: defaults.dedicated_to || '', source, interest }),
      });
      if (!res.ok) throw new Error();
      setStatus('done');
    } catch {
      setStatus('idle');
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="waitlist-dialog">
        {status === 'done' ? (
          <SuccessView onClose={() => onOpenChange(false)} />
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 text-amber-700">
                <PauseCircle className="h-5 w-5" />
                <span className="text-xs font-semibold uppercase tracking-wider">Orders paused</span>
              </div>
              <DialogTitle className="text-2xl text-gray-900 text-left">Join the Waitlist</DialogTitle>
              <DialogDescription className="text-left text-gray-600 leading-relaxed">{PAUSE_MESSAGE}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="wl-name">Your name</Label>
                <Input id="wl-name" value={form.name} onChange={set('name')} data-testid="waitlist-name-input" />
              </div>
              <div>
                <Label htmlFor="wl-email">Email</Label>
                <Input id="wl-email" type="email" value={form.email} onChange={set('email')} data-testid="waitlist-email-input" />
              </div>
              <div>
                <Label htmlFor="wl-prayer">Your prayer (optional, kept private)</Label>
                <Textarea id="wl-prayer" rows={4} value={form.prayer_text} onChange={set('prayer_text')} data-testid="waitlist-prayer-input" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="waitlist-error">{error}</p>}
              <Button type="submit" disabled={status === 'loading'} className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white text-base" data-testid="waitlist-submit-btn">
                {status === 'loading' ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Notify Me When You Reopen'}
              </Button>
              <p className="text-xs text-gray-500 text-center">No payment is taken. You can ask us to remove you at any time.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistDialog;
